import type { ClientTaskPreview } from "@common/schemas/organization-task.schema";
import { TaskStatus } from "@common/types/task";
import {
    WorkspaceAvatar,
    type WorkspaceAvatarWorkspace,
} from "@design-system/Avatar/WorkspaceAvatar";
import { getOrganizationClientTasksLink } from "@components/organizationView/clients-tasks/organizationTaskLink";
import type { SideNavStatusKind } from "@design-system/SideNav/SideNav.types";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSubItem } from "@design-system/SideNav/SideNavSubItem";
import { useSideNavMemory } from "@design-system/SideNav/useSideNavMemory";
import { timeAgo } from "@helpers/date";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useClientRowTarget } from "./useClientRowTarget";
import { useClientTaskTarget } from "./useClientTaskTarget";
import styles from "./OrgClientRow.module.scss";

const toTaskStatus = (task: ClientTaskPreview): SideNavStatusKind => {
    if (task.status === TaskStatus.IN_PROGRESS) {
        return "inProgress";
    }
    return task.hasUnreadChat ? "unread" : "none";
};

/**
 * A recent client: its name opens the client's Month-end close on the org Actions page,
 * or switches into the client without close management (see
 * `useClientRowTarget`), and its `⋯` menu holds the client's actions. With
 * `tasks` it also expands, through its icon, into rows that open each task
 * (see `useClientTaskTarget`), ending with View all.
 */
export const OrgClientRow = ({
    workspace,
    tasks,
    isExpandedByDefault,
}: {
    workspace: WorkspaceAvatarWorkspace & { id: string };
    tasks: readonly ClientTaskPreview[];
    isExpandedByDefault: boolean;
}) => {
    const { closeManagement: isCloseManagementEnabled } = useTypedFlags();
    const rowTarget = useClientRowTarget(workspace);
    const getTaskTarget = useClientTaskTarget(workspace.id);
    const [isExpanded, setIsExpanded] = useSideNavMemory({
        id: `org-clients:${workspace.id}`,
        initialValue: isExpandedByDefault,
    });
    const rowProps = {
        ...rowTarget,
        icon: <WorkspaceAvatar workspace={workspace} size="xs" aria-hidden />,
        label: workspace.name,
        labelCopyId: workspace.id,
        labelCopyLabel: "Workspace",
        className: styles.clientRow,
    };

    if (tasks.length === 0) {
        return <SideNavItem {...rowProps} />;
    }

    return (
        <SideNavItem
            {...rowProps}
            isExpanded={isExpanded}
            onExpandedChange={setIsExpanded}
        >
            {tasks.map((task) => (
                <SideNavSubItem
                    key={task.id}
                    {...getTaskTarget(task.id)}
                    isActive={false}
                    label={task.title}
                    status={toTaskStatus(task)}
                    meta={timeAgo(task.lastActivityAt, { hideAgo: true })}
                />
            ))}
            {isCloseManagementEnabled && (
                <SideNavSubItem
                    to={getOrganizationClientTasksLink({
                        workspaceId: workspace.id,
                    })}
                    isActive={false}
                    label="View all"
                />
            )}
        </SideNavItem>
    );
};
